import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowUp,
  Check,
  ChevronsUpDown,
  Flame,
  Layers,
  PencilLine,
  Search,
  Send,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { selectUser } from '@/state/selectors/root-selectors';

import {
  buildFeatureClusters,
  findSimilarFeatureRequests,
  getMomentumScore,
} from './featureFeedbackAlgorithms';
import {
  type AddFeatureRequestInput,
  FEATURE_REQUEST_STATUSES,
  type FeatureRequest,
  type FeatureRequestCategory,
  type FeatureRequestStatus,
  type RequestSortMode,
  type RequestStatusFilter,
} from './types';
import { useFeatureFeedbackBoard } from './useFeatureFeedbackBoard';

const CATEGORY_LABELS: Record<FeatureRequestCategory, string> = {
  workflow: 'Workflow',
  automation: 'Automation',
  integrations: 'Integrations',
  analytics: 'Analytics',
  performance: 'Performance',
  ux: 'UX / UI',
  other: 'Other',
};

const STATUS_LABELS: Record<FeatureRequestStatus, string> = {
  proposed: 'Proposed',
  planned: 'Planned',
  in_progress: 'In Progress',
  released: 'Released',
};

const STATUS_STYLES: Record<FeatureRequestStatus, string> = {
  proposed: 'bg-amber-500/10 text-amber-800 border-amber-300/60',
  planned: 'bg-sky-500/10 text-sky-800 border-sky-300/60',
  in_progress: 'bg-emerald-500/10 text-emerald-800 border-emerald-300/60',
  released: 'bg-zinc-500/10 text-zinc-700 border-zinc-300/60',
};

const SORT_OPTIONS: Array<{ value: RequestSortMode; label: string }> = [
  { value: 'trending', label: 'Trending' },
  { value: 'top', label: 'Top voted' },
  { value: 'newest', label: 'Newest' },
];

const DEFAULT_REQUEST_CATEGORY: FeatureRequestCategory = 'other';

const STATUS_FILTERS: Array<{ value: RequestStatusFilter; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'proposed', label: 'Proposed' },
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'released', label: 'Released' },
];

interface DropdownOption<TValue extends string> {
  value: TValue;
  label: string;
}

interface CashComboboxProps<TValue extends string> {
  value: TValue;
  options: DropdownOption<TValue>[];
  onChange: (value: TValue) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  triggerClassName?: string;
}

const CashCombobox = <TValue extends string,>({
  value,
  options,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  triggerClassName,
}: CashComboboxProps<TValue>) => {
  const [open, setOpen] = useState(false);

  const selectedOption = options.find((option) => option.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'h-9 w-full justify-between rounded-xl border border-zinc-200 bg-white px-3 text-[13px] font-semibold text-zinc-800 shadow-[0_1px_0_rgba(15,23,42,0.04)] transition-colors hover:border-zinc-300 hover:bg-zinc-50 hover:text-zinc-900 focus:ring-2 focus:ring-emerald-500/20',
            triggerClassName,
          )}
        >
          <span className="truncate">{selectedOption?.label ?? placeholder}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 text-zinc-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] rounded-xl border border-zinc-200 bg-white p-0 text-zinc-900 shadow-[0_20px_55px_-30px_rgba(15,23,42,0.45)]"
      >
        <Command className="rounded-xl bg-white text-zinc-900">
          <CommandInput
            placeholder={searchPlaceholder}
            className="h-9 bg-white text-sm font-medium text-zinc-800 placeholder:text-zinc-400"
          />
          <CommandList className="max-h-56 p-1">
            <CommandEmpty className="py-4 text-center text-sm font-medium text-zinc-500">
              {emptyText}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={`${option.label} ${option.value}`}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className="rounded-lg px-2.5 py-2 text-sm font-medium text-zinc-700 data-[selected=true]:bg-emerald-50 data-[selected=true]:text-zinc-900 aria-selected:bg-emerald-50 aria-selected:text-zinc-900"
                >
                  <Check
                    className={cn(
                      'mr-2 h-3.5 w-3.5 text-emerald-600',
                      value === option.value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const REQUEST_STATUS_OPTIONS: Array<DropdownOption<FeatureRequestStatus>> =
  FEATURE_REQUEST_STATUSES.map((status) => ({
    value: status,
    label: STATUS_LABELS[status],
  }));

function formatRelativeDate(dateIso: string): string {
  const deltaMs = Date.now() - new Date(dateIso).getTime();
  const deltaMinutes = Math.floor(deltaMs / (1000 * 60));

  if (deltaMinutes < 1) {
    return 'just now';
  }

  if (deltaMinutes < 60) {
    return `${deltaMinutes}m ago`;
  }

  const deltaHours = Math.floor(deltaMinutes / 60);
  if (deltaHours < 24) {
    return `${deltaHours}h ago`;
  }

  const deltaDays = Math.floor(deltaHours / 24);
  if (deltaDays < 8) {
    return `${deltaDays}d ago`;
  }

  return new Date(dateIso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

interface FeatureRequestCardProps {
  request: FeatureRequest;
  viewerId: string;
  groupedCount: number;
  onVote: (requestId: string) => Promise<void> | void;
  onUpdate: (
    requestId: string,
    payload: Pick<FeatureRequest, 'title' | 'description'>,
  ) => Promise<void> | void;
  onStatusChange: (
    requestId: string,
    status: FeatureRequestStatus,
  ) => Promise<void> | void;
}

const FeatureRequestCard: React.FC<FeatureRequestCardProps> = ({
  request,
  viewerId,
  groupedCount,
  onVote,
  onUpdate,
  onStatusChange,
}) => {
  const hasVoted = request.votes.some((vote) => vote.voterId === viewerId);
  const canEdit = request.createdById === viewerId;
  const [isEditing, setIsEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(request.title);
  const [draftDescription, setDraftDescription] = useState(request.description);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setDraftTitle(request.title);
      setDraftDescription(request.description);
    }
  }, [isEditing, request.description, request.title]);

  const handleSaveEdit = async () => {
    const nextTitle = draftTitle.trim();
    const nextDescription = draftDescription.trim();

    if (nextTitle.length < 6) {
      toast.error('Please write a more descriptive title (at least 6 chars).');
      return;
    }

    if (nextDescription.length < 12) {
      toast.error('Please add more context so others can evaluate it.');
      return;
    }

    try {
      setIsSavingEdit(true);
      await onUpdate(request.id, {
        title: nextTitle,
        description: nextDescription,
      });
      toast.success('Feature request updated.');
      setIsEditing(false);
    } catch (error) {
      toast.error('Could not save edits right now.');
    } finally {
      setIsSavingEdit(false);
    }
  };

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.22 }}
      whileHover={{ y: -1.5 }}
      className="rounded-xl border border-zinc-200 bg-white p-3 shadow-[0_14px_32px_-26px_rgba(15,23,42,0.55)]"
    >
      <div className="flex flex-wrap items-start gap-3">
        <button
          type="button"
          className={cn(
            'group flex w-14 shrink-0 flex-col items-center rounded-lg border px-1.5 py-1.5 transition-all',
            hasVoted
              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
              : 'border-zinc-200 bg-zinc-50/70 text-zinc-700 hover:border-emerald-200 hover:bg-emerald-50',
          )}
          onClick={async () => {
            try {
              await onVote(request.id);
            } catch (error) {
              toast.error('Could not update vote right now.');
            }
          }}
          aria-label={hasVoted ? 'Remove vote' : 'Vote for feature'}
        >
          <ArrowUp className={cn('mb-0.5 h-3.5 w-3.5', hasVoted && 'text-emerald-700')} />
          <span className="text-sm font-semibold leading-none">
            {request.votes.length}
          </span>
          <span className="mt-1 text-[10px] font-semibold uppercase tracking-wide">
            {hasVoted ? 'Voted' : 'Vote'}
          </span>
        </button>

        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
            {isEditing ? (
              <Input
                value={draftTitle}
                onChange={(event) => setDraftTitle(event.target.value)}
                className="h-8 min-w-[220px] flex-1 border-zinc-300 bg-white font-medium text-zinc-900"
              />
            ) : (
              <h3 className="text-sm font-semibold text-zinc-900">
                {request.title}
              </h3>
            )}
            <Badge
              variant="outline"
              className="border-zinc-300 bg-zinc-50 text-[10px] font-medium text-zinc-700"
            >
              {CATEGORY_LABELS[request.category]}
            </Badge>
            <Badge
              variant="outline"
              className={cn(
                'text-[10px] font-medium',
                STATUS_STYLES[request.status],
              )}
            >
              {STATUS_LABELS[request.status]}
            </Badge>
            {groupedCount > 1 && (
              <Badge
                variant="outline"
                className="border-orange-300/70 bg-orange-500/10 text-[10px] text-orange-700"
              >
                {groupedCount} similar requests
              </Badge>
            )}
            {canEdit && !isEditing && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-auto h-7 rounded-md px-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900"
                onClick={() => setIsEditing(true)}
              >
                <PencilLine className="h-3.5 w-3.5" />
                Edit
              </Button>
            )}
          </div>

          {isEditing ? (
            <Textarea
              value={draftDescription}
              onChange={(event) => setDraftDescription(event.target.value)}
              className="min-h-[84px] border-zinc-300 bg-white font-medium text-zinc-900"
            />
          ) : (
            <p className="text-sm leading-relaxed text-zinc-700">
              {request.description}
            </p>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-2.5 text-xs text-zinc-600">
            <span>By {request.createdBy}</span>
            <span>Updated {formatRelativeDate(request.updatedAt)}</span>
          </div>

          {isEditing && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                className="h-7 bg-zinc-900 px-2.5 text-xs font-semibold text-white hover:bg-zinc-800"
                onClick={handleSaveEdit}
                disabled={isSavingEdit}
              >
                {isSavingEdit ? 'Saving...' : 'Save changes'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 border-zinc-300 px-2.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
                onClick={() => {
                  setDraftTitle(request.title);
                  setDraftDescription(request.description);
                  setIsEditing(false);
                }}
                disabled={isSavingEdit}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>

        <div className="w-full sm:w-32 sm:shrink-0">
          <CashCombobox
            value={request.status}
            options={REQUEST_STATUS_OPTIONS}
            onChange={async (status) => {
              try {
                await onStatusChange(request.id, status);
              } catch (error) {
                toast.error('Could not update status right now.');
              }
            }}
            placeholder="Set status"
            searchPlaceholder="Search status..."
            emptyText="No status found."
            triggerClassName="h-8 text-xs font-semibold"
          />
        </div>
      </div>
    </motion.article>
  );
};

export const FeatureFeedbackPage: React.FC = () => {
  const user = useSelector(selectUser);
  const viewerName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.email || undefined;

  const {
    viewer,
    requests,
    totalVotes,
    activeVoterCount,
    addRequest,
    toggleVote,
    setRequestStatus,
    updateRequest,
  } = useFeatureFeedbackBoard(viewerName, user?.id);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const [sortMode, setSortMode] = useState<RequestSortMode>('trending');
  const [statusFilter, setStatusFilter] = useState<RequestStatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const draftCandidate = useMemo<FeatureRequest>(
    () => ({
      id: 'draft-candidate',
      title,
      description,
      category: DEFAULT_REQUEST_CATEGORY,
      status: 'proposed',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: viewer.name,
      createdById: viewer.id,
      votes: [],
    }),
    [description, title, viewer.id, viewer.name],
  );

  const similarDraftMatches = useMemo(() => {
    if (title.trim().length < 6) {
      return [];
    }

    return findSimilarFeatureRequests(draftCandidate, requests, 0.3, 3);
  }, [draftCandidate, requests, title]);

  const activeRequests = useMemo(
    () => requests.filter((request) => request.status !== 'released'),
    [requests],
  );

  const clusters = useMemo(
    () => buildFeatureClusters(activeRequests),
    [activeRequests],
  );

  const groupedCountByRequestId = useMemo(() => {
    const groupedCount = new Map<string, number>();
    clusters.forEach((cluster) => {
      cluster.requests.forEach((request) => {
        groupedCount.set(request.id, cluster.requests.length);
      });
    });
    return groupedCount;
  }, [clusters]);

  const filteredAndSortedRequests = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    let next = requests.filter((request) => {
      if (statusFilter === 'all') {
        return true;
      }
      return request.status === statusFilter;
    });

    if (normalizedQuery) {
      next = next.filter((request) => {
        const searchableText = `${request.title} ${request.description}`.toLowerCase();
        return searchableText.includes(normalizedQuery);
      });
    }

    return [...next].sort((left, right) => {
      if (sortMode === 'newest') {
        return (
          new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
        );
      }

      if (sortMode === 'top') {
        if (right.votes.length !== left.votes.length) {
          return right.votes.length - left.votes.length;
        }
        return (
          new Date(right.createdAt).getTime() -
          new Date(left.createdAt).getTime()
        );
      }

      const leftScore = getMomentumScore(left);
      const rightScore = getMomentumScore(right);

      if (rightScore !== leftScore) {
        return rightScore - leftScore;
      }

      return (
        new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
      );
    });
  }, [requests, searchQuery, sortMode, statusFilter]);

  const handleCreateRequest = async () => {
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();

    if (trimmedTitle.length < 6) {
      toast.error('Please write a more descriptive title (at least 6 chars).');
      return;
    }

    if (trimmedDescription.length < 12) {
      toast.error('Please add more context so others can evaluate it.');
      return;
    }

    const payload: AddFeatureRequestInput = {
      title: trimmedTitle,
      description: trimmedDescription,
      category: DEFAULT_REQUEST_CATEGORY,
    };

    try {
      const result = await addRequest(payload);

      if (result.type === 'created') {
        toast.success('Feature request submitted and up for voting.');
        setTitle('');
        setDescription('');
        return;
      }

      if (result.type === 'grouped') {
        toast.success(
          `Similar idea found, your vote was added to “${result.matchedRequest.title}”.`,
        );
        setTitle('');
        setDescription('');
        return;
      }

      toast.info(
        `You already support “${result.matchedRequest.title}”. No duplicate added.`,
      );
    } catch (error) {
      toast.error('Could not submit the request. Please try again.');
    }
  };

  return (
    <div className="relative min-h-full overflow-x-hidden bg-[radial-gradient(circle_at_10%_0%,#dff9ec_0%,transparent_35%),radial-gradient(circle_at_90%_0%,#ffe7c9_0%,transparent_30%),linear-gradient(180deg,#f8fafc_0%,#f8fdf9_100%)] text-zinc-900">
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute -left-32 -top-28 h-80 w-80 rounded-full bg-emerald-300/30 blur-3xl"
        animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 top-24 h-80 w-80 rounded-full bg-amber-300/25 blur-3xl"
        animate={{ x: [0, -26, 0], y: [0, 22, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-30 [background-image:linear-gradient(to_right,rgba(82,82,91,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(82,82,91,0.08)_1px,transparent_1px)] [background-size:36px_36px]"
      />

      <div className="relative mx-auto flex w-full max-w-[1200px] flex-col gap-4 px-3 pb-8 pt-4 font-medium sm:px-5 lg:px-6">
        <motion.section
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, delay: 0.04 }}
        >
          <Card className="overflow-hidden border-zinc-200/80 bg-white/92 shadow-[0_20px_55px_-32px_rgba(15,23,42,0.45)]">
            <CardContent className="grid gap-3 p-4 md:grid-cols-[1.2fr_0.8fr]">
              <div>
                <p className="mb-1 text-[11px] font-semibold tracking-[0.2em] text-zinc-500 uppercase">
                  App Feedback Radar
                </p>
                <h1 className="font-serif text-[30px] leading-tight text-zinc-900 sm:text-[34px]">
                  Feature ideas, grouped and voted without channel noise.
                </h1>
                <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-zinc-700">
                  Submit ideas, auto-detect duplicates, and route everyone toward
                  the same voting thread. This board now pulls live data so votes
                  and status updates stay in sync for everyone.
                </p>
              </div>

              <div className="grid gap-2 sm:grid-cols-3 md:grid-cols-1">
                <motion.div
                  whileHover={{ y: -1.5 }}
                  className="rounded-lg border border-emerald-100 bg-emerald-50/65 p-2.5"
                >
                  <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium text-zinc-700">
                    <Layers className="h-3.5 w-3.5" />
                    Active Themes
                  </div>
                  <div className="text-xl font-semibold text-zinc-900">
                    {clusters.length}
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ y: -1.5 }}
                  className="rounded-lg border border-amber-100 bg-amber-50/70 p-2.5"
                >
                  <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium text-zinc-700">
                    <Flame className="h-3.5 w-3.5" />
                    Total Votes
                  </div>
                  <div className="text-xl font-semibold text-zinc-900">
                    {totalVotes}
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ y: -1.5 }}
                  className="rounded-lg border border-sky-100 bg-sky-50/70 p-2.5"
                >
                  <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium text-zinc-700">
                    <Users className="h-3.5 w-3.5" />
                    Active Voters
                  </div>
                  <div className="text-xl font-semibold text-zinc-900">
                    {activeVoterCount}
                  </div>
                </motion.div>
              </div>
            </CardContent>
          </Card>
        </motion.section>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-4">
            <Card
              id="feature-feedback-composer"
              className="border-zinc-200/80 bg-white/92 shadow-[0_16px_42px_-30px_rgba(15,23,42,0.5)]"
            >
              <CardHeader className="space-y-1 px-4 pb-2 pt-4">
                <CardTitle className="text-lg">Propose a feature</CardTitle>
                <CardDescription className="text-[13px] text-zinc-700">
                  New requests are checked against existing ideas and grouped when
                  similarity is high.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 px-4 pb-4 pt-1">
                <div>
                  <Input
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    placeholder="Title: what should improve?"
                    className="h-9 border-zinc-200 bg-white font-medium text-zinc-900 placeholder:text-zinc-500"
                  />
                </div>

                <Textarea
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Describe the pain point and what success looks like."
                  className="min-h-[84px] border-zinc-200 bg-white font-medium text-zinc-900 placeholder:text-zinc-500"
                />

                <AnimatePresence>
                  {similarDraftMatches.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      className="rounded-lg border border-orange-200 bg-orange-50/80 p-2.5"
                    >
                      <div className="mb-1.5 text-[11px] font-semibold tracking-wide text-orange-800 uppercase">
                        Similar ideas detected
                      </div>
                      <div className="space-y-1.5">
                        {similarDraftMatches.map((match) => (
                          <div
                            key={match.request.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-orange-200/80 bg-white px-2.5 py-1.5"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-zinc-900">
                                {match.request.title}
                              </p>
                              <p className="text-xs text-zinc-600">
                                {Math.round(match.score * 100)}% similarity ·{' '}
                                {match.request.votes.length} votes
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 border-orange-200 bg-white text-zinc-700 hover:bg-orange-50"
                              onClick={async () => {
                                try {
                                  await toggleVote(match.request.id);
                                  toast.success('Vote added to similar request.');
                                } catch (error) {
                                  toast.error('Could not add vote. Please try again.');
                                }
                              }}
                            >
                              Vote this
                            </Button>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs text-zinc-600">
                    Posting as <span className="font-medium">{viewer.name}</span>
                  </p>
                  <Button
                    type="button"
                    className="h-8 bg-emerald-600 px-3.5 text-white hover:bg-emerald-500"
                    onClick={handleCreateRequest}
                  >
                    <Send className="h-4 w-4" />
                    Submit and vote
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-zinc-200/80 bg-white/92 shadow-[0_16px_42px_-30px_rgba(15,23,42,0.5)]">
              <CardContent className="space-y-3 p-3">
                <div className="grid gap-2.5 md:grid-cols-[minmax(0,1fr)_160px]">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                    <Input
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Search requests"
                      className="h-9 border-zinc-200 bg-white pl-9 font-medium text-zinc-900 placeholder:text-zinc-500"
                    />
                  </div>

                  <CashCombobox
                    value={sortMode}
                    options={SORT_OPTIONS}
                    onChange={(value) => setSortMode(value)}
                    placeholder="Sort"
                    searchPlaceholder="Search sort option..."
                    emptyText="No sorting option found."
                    triggerClassName="h-9"
                  />
                </div>

                <div className="flex flex-wrap gap-1 rounded-lg border border-zinc-200 bg-zinc-50/80 p-1">
                  {STATUS_FILTERS.map((filter) => {
                    const isActive = statusFilter === filter.value;
                    return (
                      <button
                        key={filter.value}
                        type="button"
                        onClick={() => setStatusFilter(filter.value)}
                        className={cn(
                          'rounded-md px-2.5 py-1 text-[11px] font-semibold transition-colors',
                          isActive
                            ? 'bg-zinc-900 text-white shadow-sm'
                            : 'text-zinc-700 hover:bg-zinc-200',
                        )}
                      >
                        {filter.label}
                      </button>
                    );
                  })}
                </div>

                <AnimatePresence mode="popLayout" initial={false}>
                  <div className="space-y-3">
                    {filteredAndSortedRequests.map((request) => (
                      <FeatureRequestCard
                        key={request.id}
                        request={request}
                        viewerId={viewer.id}
                        groupedCount={groupedCountByRequestId.get(request.id) ?? 1}
                        onVote={toggleVote}
                        onUpdate={updateRequest}
                        onStatusChange={setRequestStatus}
                      />
                    ))}

                    {filteredAndSortedRequests.length === 0 && (
                      <motion.div
                        layout
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="rounded-lg border border-dashed border-zinc-300 bg-white p-5 text-center text-sm text-zinc-600"
                      >
                        No requests match your current filters.
                      </motion.div>
                    )}
                  </div>
                </AnimatePresence>
              </CardContent>
            </Card>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
            <Card className="border-zinc-200/80 bg-white/92 shadow-[0_16px_42px_-30px_rgba(15,23,42,0.45)]">
              <CardHeader className="space-y-1 px-4 pb-2 pt-4">
                <CardTitle className="text-base">Auto-grouped themes</CardTitle>
                <CardDescription className="text-[13px] text-zinc-700">
                  Similar requests are merged into shared clusters to reduce noise.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-1.5 px-4 pb-4 pt-1">
                {clusters.slice(0, 6).map((cluster) => (
                  <div
                    key={cluster.id}
                    className="rounded-lg border border-zinc-200 bg-zinc-50/60 px-2.5 py-2"
                  >
                    <p className="text-sm font-medium text-zinc-900">
                      {cluster.canonicalRequest.title}
                    </p>
                    <p className="mt-0.5 text-xs text-zinc-600">
                      {cluster.totalVotes} votes · {cluster.requests.length}{' '}
                      grouped request
                      {cluster.requests.length > 1 ? 's' : ''}
                    </p>
                  </div>
                ))}

                {clusters.length === 0 && (
                  <p className="rounded-lg border border-dashed border-zinc-300 p-3 text-xs text-zinc-600">
                    No active clusters yet.
                  </p>
                )}
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
};
